// Cap upload payload client-side: phone cameras ship 4–6K originals that stall
// mobile uploads. Cap the SHORT edge (not long edge) at UPLOAD_MAX_SHORT_EDGE so
// a full-height square crop of the result still carries at least 2000px per side
// — the print crop (EditImage getCroppedCanvas, up to 2000×2000) is never handed
// fewer pixels than it can use. Files that can't decode in-browser (HEIC) pass
// through untouched; the server converts and resizes those.
export const UPLOAD_MAX_SHORT_EDGE = 2000;
export const UPLOAD_JPEG_Q = 0.88;

const loadImg = (url) =>
  new Promise((resolve) => {
    const im = new Image();
    im.onload = () => resolve(im);
    im.onerror = () => resolve(null);
    im.src = url;
  });

// Returns { file, w, h, scaled }. `file` is a re-encoded JPEG when the source
// was oversized, otherwise the original file. `w`/`h` are the dimensions of the
// returned file (0 when the browser couldn't decode it, e.g. HEIC).
export async function downscaleForUpload(file) {
  const passthrough = { file, w: 0, h: 0, scaled: false };
  if (!file) return passthrough;
  const srcUrl = URL.createObjectURL(file);
  try {
    const im = await loadImg(srcUrl);
    if (!im) return passthrough;
    const w = im.naturalWidth || im.width;
    const h = im.naturalHeight || im.height;
    if (!w || !h) return passthrough;
    if (Math.min(w, h) <= UPLOAD_MAX_SHORT_EDGE) {
      return { file, w, h, scaled: false };
    }
    const s = UPLOAD_MAX_SHORT_EDGE / Math.min(w, h);
    const cw = Math.max(1, Math.round(w * s));
    const ch = Math.max(1, Math.round(h * s));
    const canvas = document.createElement("canvas");
    canvas.width = cw;
    canvas.height = ch;
    const ctx = canvas.getContext("2d");
    if (!ctx) return { file, w, h, scaled: false };
    ctx.drawImage(im, 0, 0, cw, ch);
    const blob = await new Promise((resolve) => {
      try {
        canvas.toBlob((b) => resolve(b), "image/jpeg", UPLOAD_JPEG_Q);
      } catch (e) {
        resolve(null);
      }
    });
    if (!blob) return { file, w, h, scaled: false };
    const base = (file.name || "photo.jpg").replace(/\.(heic|heif|png|webp|avif|jpe?g)$/i, "");
    const outFile = new File([blob], `${base || "photo"}.jpg`, {
      type: "image/jpeg",
      lastModified: Date.now(),
    });
    return { file: outFile, w: cw, h: ch, scaled: true };
  } catch (e) {
    return passthrough;
  } finally {
    URL.revokeObjectURL(srcUrl);
  }
}
